<template>
  <v-row>
    <v-col>
      <AppCard :class="{ 'pt-0': $vuetify.display.mdAndDown }">
        <template v-if="paginatedDocumentsPending && paginatedDocuments.length === 0">
          <v-skeleton-loader type="table-thead, table-tbody"/>
        </template>
        <div v-else class="documents-table-wrapper">
          <!-- There is custom logic on this table so we don't need "must sort" here to have its behavior -->
          <v-data-table-server
            v-model="selectedItems"
            id="DocumentsTable"
            data-locator="documents-table"
            :headers="headers"
            :items-per-page="documentsPagination.itemsPerPage"
            :items="tableItems"
            item-selectable="isSelectable"
            item-value="selectableKey"
            :itemsLength="documentsPagination.itemCount"
            :loading="paginatedDocumentsPending"
            show-select
            return-object
            hide-default-body
            hide-default-footer
          >
            <template #[`header.data-table-select`]="{ allSelected, someSelected, selectAll }">
              <v-checkbox-btn
                base-color="icon-secondary"
                hide-details
                :model-value="allSelected"
                :indeterminate="someSelected && !allSelected"
                data-locator="select-all-checkbox"
                @update:model-value="selectAll(!allSelected)"
              />
            </template>
            <template #[`header.name`]="{ column }">
              <div class="clickable d-flex align-center" @click="handleSorting('name')">
                <span class="mr-2">{{ column.title }}</span>
                <app-material-icon
                  v-if="documentsSorting.sortBy !== 'name'"
                  name="mobiledata_arrows"
                  color="icon-secondary"
                ></app-material-icon>
                <app-material-icon
                  v-if="documentsSorting.sortBy === 'name' && documentsSorting.sortDir === 'ASC'"
                  name="arrow_downward"
                ></app-material-icon>
                <app-material-icon
                  v-if="documentsSorting.sortBy === 'name' && documentsSorting.sortDir === 'DESC'"
                  name="arrow_upward"
                ></app-material-icon>
              </div>
            </template>
            <template #[`header.size`]="{ column }">
              <div class="clickable d-flex align-center" @click="handleSorting('size')">
                <span class="mr-2">{{ column.title }}</span>
                <app-material-icon
                  v-if="documentsSorting.sortBy !== 'size'"
                  name="mobiledata_arrows"
                  color="icon-secondary"
                ></app-material-icon>
                <app-material-icon
                  v-if="documentsSorting.sortBy === 'size' && documentsSorting.sortDir === 'ASC'"
                  name="arrow_downward"
                ></app-material-icon>
                <app-material-icon
                  v-if="documentsSorting.sortBy === 'size' && documentsSorting.sortDir === 'DESC'"
                  name="arrow_upward"
                ></app-material-icon>
              </div>
            </template>
            <template #[`header.updatedBy`]="{ column }">
              <div class="clickable d-flex align-center" @click="handleSorting('last_edit_user')">
                <span class="mr-2">{{ column.title }}</span>
                <app-material-icon
                  v-if="documentsSorting.sortBy !== 'last_edit_user'"
                  name="mobiledata_arrows"
                  color="icon-secondary"
                ></app-material-icon>
                <app-material-icon
                  v-if="documentsSorting.sortBy === 'last_edit_user' && documentsSorting.sortDir === 'ASC'"
                  name="arrow_downward"
                ></app-material-icon>
                <app-material-icon
                  v-if="documentsSorting.sortBy === 'last_edit_user' && documentsSorting.sortDir === 'DESC'"
                  name="arrow_upward"
                ></app-material-icon>
              </div>
            </template>
            <template #[`header.updatedAt`]="{ column }">
              <div class="clickable d-flex align-center" @click="handleSorting('last_edit_at')">
                <span class="mr-2">{{ column.title }}</span>
                <app-material-icon
                  v-if="documentsSorting.sortBy !== 'last_edit_at'"
                  name="mobiledata_arrows"
                  color="icon-secondary"
                ></app-material-icon>
                <app-material-icon
                  v-if="documentsSorting.sortBy === 'last_edit_at' && documentsSorting.sortDir === 'ASC'"
                  name="arrow_downward"
                ></app-material-icon>
                <app-material-icon
                  v-if="documentsSorting.sortBy === 'last_edit_at' && documentsSorting.sortDir === 'DESC'"
                  name="arrow_upward"
                ></app-material-icon>
              </div>
            </template>
            <template #tbody="{ items, columns, toggleSelect } ">
              <DocumentsTableBody
                :current-folder="currentFolder"
                :columns-count="columns.length"
                :documents="items"
                :loading="paginatedDocumentsPending"
                @select="toggleSelect"
              />
            </template>
          </v-data-table-server>
        </div>
        <div class="d-flex justify-end mt-4">
          <AppPagination :current-page="documentsPagination.page"
                         :page-size="documentsPagination.pageSize"
                         :total="documentsPagination.itemCount"
                         @update:current-page="onPageChange($event)"
                         @update:page-size="onPageSizeChange($event)"
          />
        </div>
      </AppCard>
    </v-col>
  </v-row>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex'

import AppCard from '@/common/AppCard.vue'
import AppPagination from '@/common/pagination/AppPagination.vue'
import { handleElementDimensionResize } from '@/common/utils/dimensionObserver'
import { roomFileDownloadUrl } from '@/common/utils/files'
import { setUserSetting } from '@/common/utils/userSettings'
import { ROOT_FOLDER, RECENT_FOLDER } from '@/project/documents/constants/special-folders'
import {
  GET_PAGINATED_DOCUMENTS,
  SELECT_ALL_DOCUMENTS,
  SELECT_DOCUMENTS,
} from '@/store/modules/documents/action_types'
import { SET_DOCUMENTS_PAGINATION, SET_DOCUMENTS_SORTING } from '@/store/modules/documents/mutation_types'

import DocumentsTableBody from './DocumentsTableBody.vue'

export default {
  name: 'DocumentsTableTemplate',
  components: { AppPagination, AppCard, DocumentsTableBody },
  props: {
    mnemo: {
      type: String,
      required: true,
    },
  },
  data () {
    return {
      sorting: {
        name: 'basename',
        order: 'asc',
      },
      resizeObserverDestroyCallback: null,
    }
  },
  watch: {
    async dataReady (newDataReady) {
      await this.$nextTick()
      if (newDataReady) {
        this.resizeObserverDestroyCallback = handleElementDimensionResize(
          '.sticky-container', 'height', (newHeight) => {
          const headerElement = document.querySelector('.v-table__wrapper thead')
            if (headerElement) {
              headerElement.style.top = `${newHeight}px`
            }
          })
      }
    },
  },
  computed: {
    ...mapState('documents', [
      'filters',
      'getFolderPathPending',
      'loading',
      'searchLoading',
      'searchModeEnabled',
      'searchResults',
      'selectedDocuments',
      'paginatedDocuments',
      'paginatedDocumentsPending',
      'documentsPagination',
      'documentsSorting',
      'paginatedDocumentsCurrentFolderId',
    ]),
    ...mapState('documentsBreadcrumb', ['breadcrumb']),
    ...mapGetters('documents', ['isRootFolder', 'isRecentFolder']),
    ...mapGetters('room', ['isCurrentUserPm', 'roomMnemo']),
    ...mapGetters('user', ['currentUserId']),
    currentFolder () {
      return this.breadcrumb[this.breadcrumb.length - 1] ?? {
        id: ROOT_FOLDER.id,
        name: this.$t('common.documents.rootFolder'),
      }
    },
    headers () {
      const locationColumnIsEnabled = this.searchModeEnabled || this.isRecentFolder
      const columnHeaders = [
        {
          title: this.$t('project.documents.table.DocumentsTableTemplate.name'),
          sortable: false,
          value: 'name',
        },
        {
          title: this.$t('project.documents.table.DocumentsTableTemplate.size'),
          sortable: false,
          value: 'size',
        },
        {
          title: this.$t('project.documents.table.DocumentsTableTemplate.updatedAt'),
          sortable: false,
          value: 'updatedAt',
        },
        {
          title: this.$t('project.documents.table.DocumentsTableTemplate.updatedBy'),
          sortable: false,
          value: 'updatedBy',
        },
        {
          title: this.$t('project.documents.table.DocumentsTableTemplate.sharing'),
          sortable: false,
          value: 'share',
        },
        {
          title: this.$t('project.documents.table.DocumentsTableTemplate.location'),
          sortable: false,
          value: 'location',
          hidden: !locationColumnIsEnabled,
        },
        {
          title: this.$t('project.documents.table.DocumentsTableTemplate.options'),
          sortable: false,
          value: 'options',
          align: 'end',
        },
      ]

      return columnHeaders.filter(columnHeader => !columnHeader.hidden)
    },
    dataReady () {
      return !(Object.keys(this.paginatedDocuments).length === 0 || this.paginatedDocumentsPending || this.searchLoading || this.getFolderPathPending)
    },
    tableItems () {
      let items = [...this.paginatedDocuments]

      if (this.isRootFolder) {
        items = items.filter(item =>
          item.specialFolder !== 'new' &&
          item.specialFolder !== 'todo' &&
          item.specialFolder !== 'qa',
        )
      }

      return items.map(filder => ({
        ...filder,
        selectableKey: `${filder.type}-${filder.id}`,
        isSelectable: !filder.specialFolder,
      }))
    },
    selectedItems: {
      get () {
        return this.selectedDocuments
      },
      set (value) {
        this.SELECT_DOCUMENTS(value)
      },
    },
  },
  methods: {
    ...mapActions('documents', [
      SELECT_ALL_DOCUMENTS,
      SELECT_DOCUMENTS,
      GET_PAGINATED_DOCUMENTS,
    ]),
    ...mapMutations('documents', [
      SET_DOCUMENTS_PAGINATION,
      SET_DOCUMENTS_SORTING,
    ]),
    updatePagination(changes) {
      this.SET_DOCUMENTS_PAGINATION({ ...this.documentsPagination, ...changes })
    },
    onPageChange(page) {
      this.handlePaginationUpdate({ page })
    },
    onPageSizeChange(pageSize) {
      setUserSetting(
        this.currentUserId,
        'documents-items-per-page',
        pageSize,
      )

      this.handlePaginationUpdate({ page: 1, pageSize })
    },
    handlePaginationUpdate(changes) {
      this.updatePagination(changes)
      this.GET_PAGINATED_DOCUMENTS()
    },
    handleSorting (name) {
      let sorting = this.documentsSorting

      if (sorting.sortBy === name) {
        sorting.sortDir = sorting.sortDir === 'ASC' ? 'DESC' : 'ASC'
      } else {
        sorting.sortBy = name
        sorting.sortDir = 'ASC'
      }

      this.SET_DOCUMENTS_SORTING(sorting)
      this.handlePaginationUpdate({ page: 1 })
    },
    fileLink (fileId) {
      return roomFileDownloadUrl(fileId, this.roomMnemo)
    },
  },
  beforeUnmount () {
    if (this.resizeObserverDestroyCallback) {
      this.resizeObserverDestroyCallback()
    }
  },
}
</script>

<style scoped lang="scss">
@import "vuetify/settings";
@import "@/scss/border-tokens";
@import "@/scss/elevation-tokens";
@import "@/scss/typography-tokens";

/*Toggle class to fit the top with the multiple selection menu*/
:deep(.v-table__wrapper thead) {
  position: sticky;
  background-color: rgb(var(--v-theme-surface-primary));
  @include elevation('z-index-sticky');
}
:deep(.v-table__wrapper) {
  overflow: unset;
}
:deep(tbody) {
  @include text('body-md-regular');

  border-bottom: get-border();
}
:deep(#DocumentsTable) {
  position: relative;

  thead th {
    @include text('heading-xs-bold');

    color: rgb(var(--v-theme-text-primary));
  }

  tbody td {
    color: rgb(var(--v-theme-text-secondary));
  }

  .v-input--selection-controls__input {
    margin-right: 0;
  }

  table {
    border-collapse: collapse;
  }

  td,
  th {
    @media #{map-get($display-breakpoints, 'md-and-down')} {
      padding-left: 8px;
      padding-right: 8px;
    }
  }
}

:deep(.v-data-footer__icons-before) {
  display: none;
}

:deep(.v-data-footer__icons-after) {
  display: none;
}

:deep(.v-data-footer) {
  flex-direction: row-reverse;
}

:deep(.theme--light.v-text-field > .v-input__control > .v-input__slot:before) {
  border: none;
}

.data-table-simple-checkbox i {
  font-size: 16px;
}

:deep(.filderName) {
  word-break: break-word;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  line-clamp: 1;
  -webkit-box-orient: vertical;
}

@media (max-width: 960px) {
  .documents-table-wrapper {
    overflow-x: auto;
  }

  :deep(.v-table__wrapper) {
    min-width: 800px;
  }

  :deep(.v-table__wrapper thead) {
    top: 0 !important;
  }
}
</style>
